/* eslint-disable react/no-unescaped-entities */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Plus, Minus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

/* -----------------------------------------------------------------------
 * Reusable sections for every Bibliothèque HÉRITA page.
 * Identical typography, spacing and palette as the rest of the site
 * (charbon #1A2530, sand #E3D5CA, cream #F9F7F3, Cormorant Garamond,
 * Work Sans, IBM Plex Mono). Use these primitives to build new
 * educational pages without redesigning anything.
 * --------------------------------------------------------------------- */

/**
 * Single source of truth for every guide in the Bibliothèque HÉRITA.
 * Order matters: it drives the on-page "Guides associés" cluster at the
 * bottom of every guide. The hub itself (/comprendre-document-notarie)
 * is intentionally excluded — it is reached through the breadcrumb.
 */
export const GUIDE_REGISTRY = [
  { label: 'Acte de notoriété', to: '/acte-notoriete' },
  { label: 'Déclaration de succession', to: '/declaration-succession' },
  { label: 'Donation-partage', to: '/donation-partage' },
  { label: 'Testament', to: '/testament' },
  { label: 'Attestation immobilière', to: '/attestation-immobiliere' },
  { label: 'Acte de vente', to: '/acte-de-vente' },
  { label: 'SCI', to: '/sci' },
  { label: 'Indivision', to: '/indivision' },
  { label: 'Courrier notarial', to: '/courrier-notarial' },
];

/** Build the cross-link list for a given guide path — every other guide,
 *  preserving the registry order. Used by GuidePage and by the two
 *  manually-written guides for their "Guides associés" cluster. */
export const relatedGuides = (currentPath) =>
  GUIDE_REGISTRY.filter((g) => g.to !== currentPath);


export const LibraryHero = ({
  eyebrow,
  title,
  subtitle,
  ctaLabel = 'Analyser mon document',
  trustItems = [],
  tags = [],
}) => {
  const navigate = useNavigate();
  return (
    <section className="relative px-5 sm:px-6 md:px-12 lg:px-24 pt-16 sm:pt-24 pb-14 sm:pb-20 bg-[#F9F7F3]">
      <div className="max-w-3xl mx-auto text-center">
        {eyebrow && (
          <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#2C363F] mb-8 opacity-70">
            {eyebrow}
          </p>
        )}
        <h1
          className="font-['Cormorant_Garamond'] font-light text-[40px] leading-[1.08] sm:text-[60px] lg:text-[64px] text-[#1A2530] mb-6"
          style={{ letterSpacing: '-0.015em' }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="font-['Work_Sans'] font-light text-base sm:text-lg text-[#2C363F] mb-10 max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
        <Button
          onClick={() => navigate('/upload')}
          data-testid="library-hero-cta"
          className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-9 py-5 rounded-full font-['Work_Sans'] font-medium text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
        >
          {ctaLabel}
          <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>

        {trustItems.length > 0 && (
          <p className="mt-5 font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#2C363F] opacity-90">
            {trustItems.join('  •  ')}
          </p>
        )}

        {tags.length > 0 && (
          <div
            className="mt-8 flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-2xl mx-auto"
            data-testid="library-hero-tags"
          >
            {tags.map((t) => {
              const isObject = typeof t === 'object' && t !== null;
              const label = isObject ? t.label : t;
              const to = isObject ? t.to : null;
              const className =
                "inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#1A2530] bg-white border border-[#E2DBCF] rounded-full px-3 py-1 leading-none";
              if (to) {
                return (
                  <Link
                    key={label}
                    to={to}
                    aria-label={`Comprendre : ${label}`}
                    data-testid={`hero-tag-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`${className} hover:border-[#1A2530] hover:shadow-sm transition-all`}
                  >
                    {label}
                  </Link>
                );
              }
              return (
                <span key={label} className={className}>
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export const SectionHeader = ({ eyebrow, title, intro, align = 'center' }) => (
  <div
    className={`mb-10 sm:mb-14 ${align === 'center' ? 'text-center' : ''}`}
  >
    {eyebrow && (
      <p
        className={`font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 mb-4 ${align === 'center' ? '' : ''}`}
      >
        {eyebrow}
      </p>
    )}
    <h2
      className="font-['Cormorant_Garamond'] font-light text-[30px] sm:text-[40px] lg:text-[48px] text-[#1A2530] leading-[1.15]"
      style={{ letterSpacing: '-0.01em' }}
    >
      {title}
    </h2>
    {intro && (
      <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] mt-5 max-w-2xl mx-auto leading-relaxed">
        {intro}
      </p>
    )}
  </div>
);

/** Premium document card (grid item). */
export const LibraryDocumentCard = ({ Icon, title, description, to, ctaLabel = 'Comprendre' }) => (
  <Link
    to={to}
    className="group block bg-white border border-[#E2DBCF] rounded-2xl p-6 sm:p-7 hover:shadow-md hover:border-[#D3CCBE] transition-all duration-300"
    data-testid={`library-doc-card-${to.replace(/\//g, '-')}`}
  >
    <div className="w-11 h-11 rounded-full bg-[#E3D5CA] flex items-center justify-center mb-5 group-hover:bg-[#1A2530] group-hover:text-white transition-colors">
      <Icon className="h-5 w-5 text-[#1A2530] group-hover:text-white" aria-hidden="true" />
    </div>
    <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[24px] text-[#1A2530] mb-2 leading-tight">
      {title}
    </h3>
    <p className="font-['Work_Sans'] text-[14px] text-[#2C363F] leading-relaxed mb-5">
      {description}
    </p>
    <span className="inline-flex items-center font-['Work_Sans'] text-[13px] font-medium text-[#1A2530] group-hover:underline underline-offset-[5px] decoration-[#1A2530]">
      {ctaLabel}
      <ChevronRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
    </span>
  </Link>
);

/** Check item used in feature lists. */
export const FeatureCheckItem = ({ label }) => (
  <li className="flex items-start gap-3 bg-white border border-[#E2DBCF] rounded-xl p-4">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1A2530] flex items-center justify-center flex-shrink-0">
      <Check className="h-3 w-3 text-white" strokeWidth={3} aria-hidden="true" />
    </span>
    <span className="font-['Work_Sans'] text-[14.5px] text-[#1A2530] leading-snug">
      {label}
    </span>
  </li>
);

/** Single FAQ row (accordion). */
const FAQRow = ({ q, a, idx, openIdx, setOpenIdx }) => {
  const isOpen = idx === openIdx;
  return (
    <div className="border-b border-[#E2DBCF] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpenIdx(isOpen ? -1 : idx)}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${idx}`}
        className="w-full text-left py-5 sm:py-6 flex items-start gap-4 hover:opacity-90 transition-opacity"
        data-testid={`library-faq-q-${idx}`}
      >
        <span className="flex-1 font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[22px] text-[#1A2530] leading-snug">
          {q}
        </span>
        <span
          className={`mt-1 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
            isOpen ? 'bg-[#1A2530] text-white' : 'bg-[#E3D5CA] text-[#1A2530]'
          }`}
          aria-hidden="true"
        >
          {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        </span>
      </button>
      {isOpen && (
        <div
          id={`faq-panel-${idx}`}
          className="pb-6 font-['Work_Sans'] text-[14.5px] text-[#2C363F] leading-[1.75] pr-12"
        >
          {a}
        </div>
      )}
    </div>
  );
};

export const LibraryFAQ = ({ items = [], defaultOpen = 0 }) => {
  const [openIdx, setOpenIdx] = useState(defaultOpen);
  return (
    <div className="max-w-3xl mx-auto" data-testid="library-faq">
      {items.map((item, i) => (
        <FAQRow
          key={i}
          idx={i}
          q={item.q}
          a={item.a}
          openIdx={openIdx}
          setOpenIdx={setOpenIdx}
        />
      ))}
    </div>
  );
};

/**
 * Meta band displayed just below the hero of any educational guide.
 * Shows reading time, last update date and an anchor-link table of contents.
 *
 * Props:
 *  - readingTime: e.g. "7 min"
 *  - lastUpdated: e.g. "18 février 2026"
 *  - sections: [{ id, label }] — each label is a click-to-scroll anchor
 */
export const GuideMeta = ({ readingTime, lastUpdated, sections = [] }) => {
  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 24;
      window.scrollTo({ top, behavior: 'smooth' });
      // update hash without jump
      if (window.history?.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
  };

  return (
    <section
      className="px-5 sm:px-6 md:px-12 lg:px-24 py-7 sm:py-8 bg-white border-b border-[#E2DBCF]"
      aria-label="Sommaire du guide"
      data-testid="guide-meta"
    >
      <div className="max-w-3xl mx-auto">
        {(readingTime || lastUpdated) && (
          <div
            className="flex flex-wrap items-center justify-center sm:justify-between gap-x-6 gap-y-2 mb-6 font-['IBM_Plex_Mono'] text-[10.5px] sm:text-[11px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80"
          >
            {readingTime && (
              <span data-testid="guide-reading-time">
                {readingTime} de lecture
              </span>
            )}
            {lastUpdated && (
              <span data-testid="guide-last-updated">
                Mis à jour le {lastUpdated}
              </span>
            )}
          </div>
        )}
        {sections.length > 0 && (
          <nav aria-label="Sommaire" data-testid="guide-toc">
            <p className="text-center font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-60 mb-4">
              Sommaire
            </p>
            <ul className="flex flex-wrap justify-center gap-2 list-none p-0">
              {sections.map(({ id, label }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(e) => handleClick(e, id)}
                    data-testid={`toc-${id}`}
                    className="inline-flex items-center font-['Work_Sans'] text-[12.5px] sm:text-[13px] text-[#1A2530] bg-[#F9F7F3] border border-[#E2DBCF] rounded-full px-3.5 py-1.5 leading-none hover:border-[#1A2530] hover:bg-white transition-all"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </section>
  );
};

/**
 * Official Sources block — emitted at the very end of every guide, just
 * before the "Guides associés" cluster. The links point to deep pages
 * of trusted French legal/governmental sources (Service-Public.fr,
 * Légifrance, Notaires de France). They open in a new tab with
 * `rel="noopener noreferrer"` to preserve user context and security.
 *
 * Props:
 *   - sources: [{ name, label, description, url, type? }]
 *      • name   — short source name (e.g. "Service-Public.fr")
 *      • label  — page title under that source (e.g. "Déclaration de succession")
 *      • description — 1 sentence explaining what the user will find
 *      • url    — deep link
 *      • type   — optional tag ("Service public", "Code civil", "Notariat")
 */
export const OfficialSources = ({ sources = [] }) => {
  if (!Array.isArray(sources) || sources.length === 0) return null;
  return (
    <section
      data-testid="official-sources"
      aria-labelledby="official-sources-heading"
      className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-[#F9F7F3] border-t border-[#E2DBCF]"
    >
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          eyebrow="Crédibilité"
          title="Sources officielles et références"
          intro="Pour aller plus loin, voici les sources publiques françaises sur lesquelles s'appuie ce guide. Tous les liens ouvrent dans un nouvel onglet."
        />
        <ul className="list-none p-0 m-0 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {sources.map(({ name, label, description, url, type }) => (
            <li
              key={url}
              className="bg-white border border-[#E2DBCF] rounded-2xl p-5 sm:p-6"
              data-testid={`source-${(name || 'src').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <p className="font-['IBM_Plex_Mono'] text-[9.5px] uppercase tracking-[0.25em] text-[#2C363F] opacity-70 mb-2">
                {type || name}
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-['Cormorant_Garamond'] text-[19px] sm:text-[21px] text-[#1A2530] leading-tight font-medium hover:underline underline-offset-4 decoration-[#1A2530] decoration-1"
              >
                {label}
              </a>
              <p className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] leading-relaxed mt-2 opacity-90">
                {description}
              </p>
              <p className="font-['Work_Sans'] text-[11px] text-[#2C363F] opacity-60 mt-3 italic">
                {name}
              </p>
            </li>
          ))}
        </ul>
        <p className="font-['Work_Sans'] text-[12px] text-[#2C363F] opacity-70 italic text-center mt-8 leading-relaxed max-w-2xl mx-auto">
          HÉRITA ne se substitue pas à votre notaire. Ces ressources publiques
          vous permettent de croiser les informations et d'approfondir chaque
          point selon votre situation.
        </p>
      </div>
    </section>
  );
};

export const LibraryFinalCta = ({ title, subtitle, ctaLabel = 'Analyser mon document' }) => {
  const navigate = useNavigate();
  return (
    <section className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-24 bg-[#F9F7F3]">
      <div className="max-w-3xl mx-auto bg-white border border-[#E2DBCF] rounded-2xl p-10 sm:p-14 text-center shadow-sm">
        <h2
          className="font-['Cormorant_Garamond'] font-light text-[28px] sm:text-[40px] lg:text-[44px] text-[#1A2530] leading-[1.15] mb-5"
          style={{ letterSpacing: '-0.01em' }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="font-['Work_Sans'] text-[15px] sm:text-base text-[#2C363F] mb-9 max-w-xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
        <Button
          onClick={() => navigate('/upload')}
          data-testid="library-final-cta"
          className="bg-[#1A2530] hover:bg-[#2C363F] text-white px-10 py-5 rounded-full font-['Work_Sans'] font-medium text-[15px] transition-all duration-300 shadow-md hover:shadow-lg"
        >
          {ctaLabel}
          <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
};
